/**
 * The recruiter-only "Workflow settings" card (plan 13) — the operational JobForm fields that make no sense to a
 * self-service customer ("owner"? their agency contact?). Rendered only for elevated roles; customers get these
 * derived at publish instead. Lives as its own component so the users-list query never fires for customers (the
 * companies query is panel-level — both personas need it for the company derivation).
 */

import { useFeatureFlagEnabled } from '@/lib/posthog/hooks/useFeatureFlagEnabled';
import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { roles } from '@/modules/auth/roles';
import { mapUsersToSelectOptions } from '@/modules/jobs/components/jobForm/userSelectOptions';
import {
  getJobStateMeta,
  jobStateOptions,
  parseJobState,
} from '@/modules/jobs/jobStates';
import type { WorkflowDraft } from '@/modules/jobs/signal/types';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/modules/ui/components/card/Card';
import { Label } from '@/modules/ui/components/label/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/ui/components/select/Select';
import { Switch } from '@/modules/ui/components/switch/Switch';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { MultiSelect } from '@/modules/ui/MultiSelect';
import { useUsersQuery } from '@/modules/users/queries/useUsersQuery';

/**
 * The boolean workflow toggles. On CREATE the `defaultValue` shows until the recruiter flips one — it mirrors
 * finlay-api `JobService.Create` (`?? false` / follow-up `?? true`), and an untouched toggle is OMITTED from the
 * request so that method stays the source of truth (keep in sync). On EDIT every toggle arrives seeded from the
 * loaded job, so the fallback never shows and values are sent as explicit echoes-or-edits.
 */
interface WorkflowToggleConfig {
  key: keyof Pick<
    WorkflowDraft,
    | 'isMessagingAutomated'
    | 'isReachOutAutomated'
    | 'isShortlistingAutomated'
    | 'isInvitationFollowUpEnabled'
    | 'isPrescreeningEnabled'
    | 'isPublicCandidateListEnabled'
  >;
  label: string;
  defaultValue: boolean;
}

const AUTOMATION_TOGGLES: WorkflowToggleConfig[] = [
  {
    key: 'isMessagingAutomated',
    label: 'Auto-message candidates',
    defaultValue: false,
  },
  {
    key: 'isReachOutAutomated',
    label: 'Automated reach-out',
    defaultValue: false,
  },
  {
    key: 'isShortlistingAutomated',
    label: 'Automated shortlisting',
    defaultValue: false,
  },
  {
    key: 'isInvitationFollowUpEnabled',
    label: 'Invitation follow-up',
    defaultValue: true,
  },
  { key: 'isPrescreeningEnabled', label: 'Prescreening', defaultValue: false },
];

const PUBLIC_SHORTLIST_TOGGLE: WorkflowToggleConfig = {
  key: 'isPublicCandidateListEnabled',
  label: 'Public candidate list',
  defaultValue: false,
};

export interface WorkflowSettingsCardProps {
  value: WorkflowDraft;
  onChange: (patch: Partial<WorkflowDraft>) => void;
  companyOptions: { value: string; label: string }[];
  /** single-tenant recruiters have their company auto-selected — no picker to show */
  showCompanyPicker: boolean;
  /** edit mode hides the create-only public-list toggle and shows the lifecycle & outreach chunk (plan 13h) */
  mode: 'create' | 'edit';
  /** locks the coordination controls — the backend rejects changes once a coordination process exists */
  hasCoordinationProcess?: boolean;
  /**
   * Hides the job-state select while the job is a Draft: a draft's only exit is the gated Publish button —
   * flipping `state` to an HM-visible value would put an incomplete brief on every HM dashboard past the
   * save-level validation.
   */
  isDraft?: boolean;
  /**
   * The job's ORIGINAL strategy when it's a legacy (non-Agent) job — keys the migration select so it stays
   * mounted (and revertible) after picking Agent, instead of vanishing pre-save as if already applied.
   */
  legacySourcingStrategy?: NonNullable<WorkflowDraft['sourcingStrategy']>;
}

export function WorkflowSettingsCard({
  value,
  onChange,
  companyOptions,
  showCompanyPicker,
  mode,
  hasCoordinationProcess = false,
  isDraft = false,
  legacySourcingStrategy,
}: WorkflowSettingsCardProps) {
  const usersQuery = useUsersQuery();
  const userOptions = mapUsersToSelectOptions(usersQuery.data ?? []);
  const isCoordinationEnabled =
    useFeatureFlagEnabled('newCoordinationAgentSettings') === true;
  // The backend requires the coordination pair to hold the matching role INSIDE the job's tenant (it 400s
  // otherwise), so mirror JobForm's tenant+role filtering rather than offering the full users list.
  const coordinationUsers = value.companyId
    ? (usersQuery.data ?? []).filter((user) =>
        (user.tenantIds ?? []).includes(value.companyId),
      )
    : [];
  const recruiterOptions = mapUsersToSelectOptions(
    coordinationUsers.filter((user) =>
      (user.roles ?? []).includes(roles.Recruiter),
    ),
  );
  const hiringManagerOptions = mapUsersToSelectOptions(
    coordinationUsers.filter((user) =>
      (user.roles ?? []).includes(roles.HiringManager),
    ),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workflow settings</CardTitle>
        <CardDescription>
          How Finlay runs this job — customers never see these.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {showCompanyPicker ? (
            <LabeledSelect
              label="Company"
              value={value.companyId}
              // switching company invalidates tenant-scoped coordination picks — clear them (mirrors JobForm)
              onChange={(companyId) =>
                onChange({
                  companyId,
                  coordinationRecruiterId: '',
                  coordinationHiringManagerId: '',
                })
              }
              options={companyOptions}
              placeholder="Select the customer"
            />
          ) : null}
          <LabeledSelect
            label="Owner"
            value={value.ownerId}
            onChange={(ownerId) => onChange({ ownerId })}
            options={userOptions}
            placeholder="Select the owner"
          />
          <div className="space-y-1.5">
            <Label>Team members</Label>
            <MultiSelect
              options={userOptions}
              defaultValue={value.userIds}
              onValueChange={(userIds) => onChange({ userIds })}
              placeholder="Add teammates"
              hideSelectAll
              maxCount={3}
              className="h-auto min-h-9"
            />
          </div>
        </div>

        <PermissionsGate permissions={[permissions.Messaging.Write]}>
          <div className="space-y-3">
            {AUTOMATION_TOGGLES.map((toggle) => (
              <WorkflowToggle
                key={toggle.key}
                config={toggle}
                value={value}
                onChange={onChange}
              />
            ))}
          </div>
        </PermissionsGate>

        {mode === 'create' ? (
          <WorkflowToggle
            config={PUBLIC_SHORTLIST_TOGGLE}
            value={value}
            onChange={onChange}
          />
        ) : null}

        {mode === 'edit' && (!isDraft || legacySourcingStrategy) ? (
          <div className="space-y-4 border-t pt-4">
            {/* Lifecycle controls stay under Jobs.Update; they do not change candidate communication. */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              {!isDraft ? (
                <div>
                  <LabeledSelect
                    label="Job state"
                    value={value.state ?? ''}
                    onChange={(raw) => {
                      const state = parseJobState(raw);
                      if (state) onChange({ state });
                    }}
                    options={jobStateOptions}
                    placeholder="Select"
                  />
                  {value.state ? (
                    <p className="text-muted-foreground mt-1.5 text-xs">
                      {getJobStateMeta(value.state)?.description}
                    </p>
                  ) : null}
                </div>
              ) : null}
              {legacySourcingStrategy ? (
                <div>
                  <LabeledSelect
                    label="Sourcing strategy"
                    value={value.sourcingStrategy ?? legacySourcingStrategy}
                    // keyed on the JOB's original strategy so the select stays mounted — and revertible —
                    // after picking Agent; the migration itself is one-way only once SAVED
                    onChange={(raw) =>
                      onChange({
                        sourcingStrategy:
                          raw === 'Agent' ? 'Agent' : legacySourcingStrategy,
                      })
                    }
                    options={[
                      {
                        value: legacySourcingStrategy,
                        label:
                          legacySourcingStrategy === 'Worker'
                            ? 'Classic (legacy)'
                            : 'Scout (legacy)',
                      },
                      { value: 'Agent', label: 'Sourcing Agent' },
                    ]}
                    placeholder="Select"
                  />
                  {value.sourcingStrategy === 'Agent' ? (
                    <p className="text-muted-foreground mt-1.5 text-xs">
                      Saving makes this permanent and starts the agent search.
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {mode === 'edit' ? (
          <PermissionsGate permissions={[permissions.Messaging.Write]}>
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="workflow-messaging-creative">
                  Creative messaging
                </Label>
                <Switch
                  id="workflow-messaging-creative"
                  checked={value.isMessagingCreative ?? true}
                  onCheckedChange={(checked) =>
                    onChange({ isMessagingCreative: checked })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="workflow-reachout-template">
                  Reach-out message template
                </Label>
                <Textarea
                  id="workflow-reachout-template"
                  rows={4}
                  value={value.initialReachOutMessageTemplate}
                  onChange={(event) =>
                    onChange({
                      initialReachOutMessageTemplate: event.target.value,
                    })
                  }
                  placeholder="Optional — the first message sent to candidates. Clearing it removes the template."
                />
              </div>
            </div>
          </PermissionsGate>
        ) : null}

        {isCoordinationEnabled ? (
          <PermissionsGate permissions={[permissions.Messaging.Write]}>
            <div className="space-y-3 border-t pt-4">
              <div className="flex items-center justify-between">
                <Label htmlFor="workflow-coordination">
                  Track coordination events
                  {hasCoordinationProcess ? (
                    <span className="text-muted-foreground ml-2 text-xs font-normal">
                      locked — a coordination process exists
                    </span>
                  ) : null}
                </Label>
                <Switch
                  id="workflow-coordination"
                  checked={value.isTrackingCoordinationEvents ?? false}
                  disabled={hasCoordinationProcess}
                  onCheckedChange={(checked) =>
                    onChange({ isTrackingCoordinationEvents: checked })
                  }
                />
              </div>
              {value.isTrackingCoordinationEvents ? (
                <>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="workflow-coordination-auto">
                      Auto-approve coordination actions
                    </Label>
                    <Switch
                      id="workflow-coordination-auto"
                      checked={value.isCoordinationActionAutoApproved ?? false}
                      disabled={hasCoordinationProcess}
                      onCheckedChange={(checked) =>
                        onChange({ isCoordinationActionAutoApproved: checked })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <LabeledSelect
                      label="Coordination recruiter"
                      value={value.coordinationRecruiterId}
                      onChange={(coordinationRecruiterId) =>
                        onChange({ coordinationRecruiterId })
                      }
                      options={recruiterOptions}
                      placeholder="Select"
                      disabled={hasCoordinationProcess}
                    />
                    <LabeledSelect
                      label="Coordination hiring manager"
                      value={value.coordinationHiringManagerId}
                      onChange={(coordinationHiringManagerId) =>
                        onChange({ coordinationHiringManagerId })
                      }
                      options={hiringManagerOptions}
                      placeholder="Select"
                      disabled={hasCoordinationProcess}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </PermissionsGate>
        ) : null}
      </CardContent>
    </Card>
  );
}

function WorkflowToggle({
  config,
  value,
  onChange,
}: {
  config: WorkflowToggleConfig;
  value: WorkflowDraft;
  onChange: (patch: Partial<WorkflowDraft>) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <Label htmlFor={`workflow-${config.key}`}>{config.label}</Label>
      <Switch
        id={`workflow-${config.key}`}
        checked={value[config.key] ?? config.defaultValue}
        onCheckedChange={(checked) => onChange({ [config.key]: checked })}
      />
    </div>
  );
}

/** One labeled single-select — the card's pickers share this shape (mirrors the panel's local `Field`). */
function LabeledSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={value} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
